import React, { useState, useMemo, useContext, useEffect } from "react";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import {
  TextField,
  PrimaryButton,
  IconButton,
  TooltipHost,
} from "@fluentui/react";
import { Icon } from "@fluentui/react/lib/Icon";
import userContext from "../../context/users/UserContext";
import loaderContext from "../../context/loader/LoaderContext";
import { forceLogout, getOnlineQcSupervisor, getOnlineRadiologist } from "../../service/AnalyticsService";
import CustomToolTipHost from "../../components/CustomToolTipHost/CustomToolTipHost";
import themeContext from "../../context/theme/ThemeContext";
import Success from "../../components/Toast_v2/Icons/Success";
import Error from "../../components/Toast_v2/Icons/Error";
import Info from "../../components/Toast_v2/Icons/Info";
import { t } from "i18next";
import toastContext from "../../context/toast/ToastContext";
import ConfirmModal from "../../components/Modal/ConfirmModal";
import { formatNowForUser, getFormatedDate } from "../IssuesAndTroubleshooting/helper/helper";


const OnlineQcSupervisor = (props) => {
  const { firmId } = useContext(userContext);
  const { ref, setLoaderRunning } = useContext(loaderContext);
  const { themeName, getAlterColor } = useContext(themeContext);

  const [filterValue, setFilterValue] = useState("");
  const [initialData, setInitialData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [allowForceLogout,setAllowForceLogout] = useState(false);
  const [unassignStudies, setUnassignStudies] = useState(false);
  const [selectedUsername, setSelectedUsername] = useState(null);
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
const [list, setList] = useState([]);
const [progressList, setProgressList] = useState([]);
const { showToastv2 } = useContext(toastContext);

const TOAST_TYPE = {
  SUCCESS: "success",
  ERROR: "error",
  INFO: "info",
};


  const handleRefresh = async () => {
    try {
      await fetchOnlineQcSupervisor();
      setLastRefreshed(formatNowForUser());
    } catch (error) {
      console.error("Refresh failed", error);
    }
  };

  const fetchOnlineQcSupervisor = async () => {
    setLoaderRunning(true);
    ref.current.continuousStart();
    const response = await getOnlineQcSupervisor(firmId);
    console.log("response for online qc supervisor =======>", response);
    if (response.data.message === "SUCCESS") {
      console.log(
        "response for online radiologist data =======>",
        response.data
      );

      const onlineData = response.data?.data.response || [];
      const forceLogout = response.data?.data.allowLogout;
      setAllowForceLogout(forceLogout);
      setInitialData(onlineData);
      setFilteredData(onlineData);
      setLoaderRunning(false);
      ref.current.complete();
    }
  };

  const handleSearch = () => {
    if (filterValue.trim() === "") {
      setFilteredData(filteredData);
      return;
    }
    const lowerFilter = filterValue.toLowerCase();
    const result = filteredData.filter(
      (item) =>
        item.firstName.toLowerCase().includes(lowerFilter) ||
        item.lastName.toLowerCase().includes(lowerFilter)
    );
    setFilteredData(result);
  };

  const handleClear = () => {
    setFilterValue("");
    setFilteredData(initialData);
  };

  const formatDate = (date) => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear().toString().slice(-2);
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${day}-${month}-${year} ${hours
      .toString()
      .padStart(2, "0")}:${minutes} ${ampm}`;
  };

  useEffect(() => {
    props.setIsOnlineRadiologistSelected(true);
    const now = new Date();
    setLastRefreshed(formatNowForUser());
    fetchOnlineQcSupervisor();
  }, []);

  const formatLastLogin = (dateString) => {
    return getFormatedDate(dateString, "DD-MMM-YY hh:mm A");
  };
  const formatCaseList = (cases) => {
    if (!cases || cases.length === 0) return { displayed: "", tooltip: "" };
  
    const caseList = cases
      .filter((item) => item && (item.orderIds || item.patientIds))
      .map((item) => item.orderIds || item.patientIds);
  
    const fullText = caseList.join(", ");
    const displayedText =
      fullText.length > 50 ? fullText.substring(0, 50) + "..." : fullText;
  
    return {
      displayed: displayedText,
      tooltip: fullText,
    };
  };
  

  const searchInputStyle = {
    root: {
      width: "250px",
      position: "relative",
    },
    fieldGroup: {
      paddingRight: "32px",
    },
  };

  const columns = useMemo(
    () => [
      {
        id: "actions",
        header: "",
        accessorKey: "_actions",
        cell: ({ row }) => {
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-evenly",
              }}
            >
              <TooltipHost content={"online"}>
                <div
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: "green",
                    margin: "2px",
                  }}
                />
              </TooltipHost>
              {allowForceLogout && <Icon
                iconName="SignOut"
                style={{ cursor: "pointer" }}
                title="Logout"
                onClick={() => 
                  // handleForceLogout(row.original.username)}
                  handleForceLogoutConfirmation(row.original.username)}
              /> }
            </div>
          );
        },
      },

      {
        id: "name",
        header: () => (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div>{t("analytics.qc-supervisor-name")}</div>
            <div>(Username)</div>
          </div>
        ),
        accessorFn: (row) => `${row.firstName} ${row.lastName} (${row.username})`,
        cell: ({ row }) => {
          const fullName = `${row.original.firstName} ${row.original.lastName} (${row.original.username})`;
          const displayName =
            fullName.length <= 29
              ? fullName
              : fullName.substring(0, 25) + "...";

          return (
            <div>
              <span>
                <TooltipHost content={fullName}>{displayName}</TooltipHost>
              </span>
            </div>
          );
        },
      },
      {
        id: "totalReported",
        header: () => (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div>{t("analytics.qc-passed-today")}</div>
            <div>(XR/CT/MR/OTH)</div>
          </div>
        ),
        accessorKey: "totalReported",
        cell: ({ getValue }) => {
          const value = getValue();
          return (
            <div>
              ({value?.XRay ?? 0} / {value?.CT ?? 0} / {value?.MR ?? 0} / {value?.Other ?? 0})
            </div>
          );
        },
      },
      {
        id: "totalAssigned",
        header: () => (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div>{t("analytics.pending")}</div>
            <div>(XR/CT/MR/OTH)</div>
          </div>
        ),
        accessorKey: "totalAssigned",
        cell: ({ getValue }) => {
          const value = getValue();
          return (
            <div>
              ({value?.XRay ?? 0} / {value?.CT ?? 0} / {value?.MR ?? 0} / {value?.Other ?? 0})
            </div>
          );
        },
      },
      {
        id: "lastLogin",
        header: t("analytics.last-login-time"),
        accessorKey: "lastLogin",
        cell: ({ getValue }) => formatLastLogin(getValue()),
      },
      {
        id: "cases",
        header: t("analytics.case-list"),
        accessorKey: "cases",
        cell: ({ getValue }) => {
          const value = getValue();
          const { displayed, tooltip } = formatCaseList(value);
          return <TooltipHost content={tooltip}>{displayed}</TooltipHost>;
        },
      },
    ],
    [allowForceLogout]
  );

  const handleForceLogoutConfirmation= async(username) =>{
    setUnassignStudies(false);
    setShowLogoutConfirmation(true);
    setSelectedUsername(username);
  }
  const handleForceLogout = async (username,unassignStudies) => {
  try {
    setLoaderRunning(true);
    ref.current.continuousStart();

    const response = await forceLogout(username,unassignStudies);

    if (response?.status === 200) {
      // toast.success(`User ${username} has been logged out.`);
      showToastv2(TOAST_TYPE.SUCCESS, `User ${username} has been logged out.`);
      fetchOnlineQcSupervisor(); // Optional refresh
    } else {
      showToastv2(TOAST_TYPE.ERROR, "Failed to log out user.");
    }
  } catch (err) {
    showToastv2(TOAST_TYPE.ERROR, "Something went wrong.");
    console.error("Logout error:", err);
  } finally {
    setLoaderRunning(false);
    ref.current.complete();
  }
};

  const table = useReactTable({
    columns,
    data: filteredData,
    getCoreRowModel: getCoreRowModel(),
  });

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;

  return (
    <>
    {showLogoutConfirmation && (
        <ConfirmModal
          showConfirmModal={showLogoutConfirmation}
          title={t("force-logout.title")}
          handleNotConfirm={() => setShowLogoutConfirmation(false)}
          cancelButtonText={t("confirm-message.no")}
          handleConfirm={ async () => {
            setShowLogoutConfirmation(false);
            handleForceLogout(selectedUsername,unassignStudies);
            
          }}
          showCheckbox={true}
          checkboxLabel={t("force-logout.unassign-studies")}
          checkboxChecked={unassignStudies}
          onCheckboxChange={(checked) => setUnassignStudies(checked)}
          confirmButtonText={t("confirm-message.yes")}
          message={t("force-logout.force-logout-cofirm-message")}
        />
      )}
    <div id="onlineRadiologist" style={{ padding: "12px 16px" }}>
      <div
        id={2}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ position: "relative" }}>
            <TextField
              placeholder="QC Supervisor Name"
              styles={searchInputStyle}
              value={filterValue}
              onChange={(e, newValue = "") => {
                setFilterValue(newValue);
                const lowerFilter = newValue.toLowerCase();
                const filtered = initialData.filter(
                  (item) =>
                    item.firstName.toLowerCase().includes(lowerFilter) ||
                    item.lastName.toLowerCase().includes(lowerFilter)
                );
                setFilteredData(filtered);
              }}
            />

            {filterValue && (
              <IconButton
                iconProps={{ iconName: "Cancel" }}
                title="Clear"
                ariaLabel="Clear"
                onClick={handleClear}
                style={{
                  position: "absolute",
                  right: "0",
                  top: "0",
                  zIndex: 1,
                  background: "transparent",
                  color: "#999",
                  cursor: "pointer",
                }}
              />
            )}
          </div>
          <PrimaryButton
            iconProps={{ iconName: "Search" }}
            onClick={handleSearch}
            styles={{ root: { minWidth: "0px" } }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {lastRefreshed && (
            <span style={{ fontSize: "14px" }}>
              Last Updated: {lastRefreshed}
            </span>
          )}
          <PrimaryButton
            iconProps={{
              iconName: "Refresh",
              cursor: "pointer",
            }}
            styles={{ root: { minWidth: "0px" } }}
            onClick={handleRefresh}
          ></PrimaryButton>
        </div>
      </div>

      <div style={{ height: "calc(100vh - 229px)" }}>
  {rows.length === 0 ? (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      color: getAlterColor("#FFFFFF","#000000"),
      fontSize: '14px',
      borderRadius: '4px'
    }}>
      {t("analytics.qc-supervisor-offline-message")}
    </div>
  ) : (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid #585858",
            borderRadius: "4px",
            overflow: "hidden",
            tableLayout: "fixed",
          }}
          className="list-view-table"
        >
          <thead className="list-view-thead">
            {headerGroups.map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className="list-view-thead-tr"
                style={{ padding: "0", fontSize: "9px" }}
              >
                {headerGroup.headers.map((column, colIndex) => (
                  <th
                    key={column.id}
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #585858",
                      fontWeight: "600",
                      width:
                        colIndex === 0
                          ? "5%"
                          : colIndex === 1
                          ? "20%"
                          : colIndex === 2
                          ? "19%"
                          : colIndex === 3
                          ? "18%"
                          : colIndex === 4
                          ? "16%"
                          : "22%",
                      padding: "6px 10px",
                      borderRight: colIndex < 5 ? "1px solid #585858" : "none",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                      }}
                    >
                      {/* OLD (crashed in v8): column.columnDef is undefined — columnDef lives on header.column
                          {flexRender(column.columnDef.header, column.getContext())} */}
                      {/* NEW: access columnDef via header.column */}
                      {flexRender(column.column.columnDef.header, column.getContext())}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody
            className="list-view-tbody modal-scroll"
          >
            {
              rows.map((row) => (
                <tr
                  key={row.id}
                  style={{
                    borderBottom: "1px solid #585858",
                    padding: "2px",
                  }}
                  className="list-view-tr hover-list-view-tr tr-alternative-color"
                >
                  {row.getVisibleCells().map((cell, colIndex) => (
                    <td
                      key={cell.id}
                      style={{
                        textAlign: "left",
                        width:
                          colIndex === 0
                            ? "5%"
                            : colIndex === 1
                            ? "20%"
                            : colIndex === 2
                            ? "19%"
                            : colIndex === 3
                            ? "18%"
                            : colIndex === 4
                            ? "16%"
                            : "22%",
                        borderRight: colIndex < 5 ? "1px solid #585858" : "none",
                        padding: "6px 10px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "clamp(8px, 0.8vw, 12px)",
                          padding: "0",
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            }
          </tbody>
        </table>  )}
      </div>
    </div>
  </>
  );

};

export default OnlineQcSupervisor;
